// Library dependencies
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

// Internal dependencies
import { driverJs } from "@/lib/driver";
import { loginSchema } from "../schemas/auth.schema";
import { useAuth } from "@/providers/auth.provider";
import { useLoginUser } from "../queries/auth.create.queries";
import { AUTH_ERRORS } from "@/constants/auth.errors";

// Types
import { LoginInput } from "@/types/auth.types";
import { AuthState } from "@/types/auth.types";
import { sleep } from "@/lib/utils";

export const useLogin = () => {
  const navigate = useNavigate();

  const { setAuthState, refetch } = useAuth();

  const { mutateAsync: loginUser, isPending } = useLoginUser();

  const [error, setError] = useState<string | null>(null);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginInput) => {
    setAuthState(AuthState.AUTHENTICATING);

    setError(null);

    try {
      const response = await loginUser(data);

      if (response.success) {
        toast.success("Successfully logged in!");

        await refetch();

        await navigate({
          to: "/dashboard",
          replace: true,
        });
        await sleep(200);
        driverJs.drive();
        return;
      }

      setAuthState(AuthState.UNAUTHENTICATED);
      setError(response.error || AUTH_ERRORS.INVALID_CREDENTIALS);
    } catch (error) {
      setAuthState(AuthState.UNAUTHENTICATED);
      setError(AUTH_ERRORS.INVALID_CREDENTIALS);
    }
  };

  return {
    form,
    onSubmit,
    isLoading: isPending,
    error,
  };
};
